import {
    StickyNote2Outlined as StickyNote2OutlinedIcon,
    WorkspacePremiumRounded as WorkspacePremiumRoundedIcon,
} from '@mui/icons-material';
import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { useContext, useState } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { ProjectHistoryItem } from '~/shared/data-contract/project-history';

import { CardIconCaption } from '../../../../design/components/cards/CardIconCaption';
import { InlineLoadingSpinner } from '../../../../design/components/LoadingSpinner';
import { useProjectHistoryQuery } from '../../../query-hooks/users/project-history';
import { useUserHistoryOptionsQuery } from '../../../query-hooks/users/user-history';
import { useScrollToLocationHash } from '../../../use-scroll-to-location-hash';
import { createUserProjectHistoryUrl, editUserProjectHistoryUrl } from '../constants';
import { AddButton } from '../user-profile/common/ActionButtons';
import { CardPlaceholder } from './common/CardPlaceholder';
import { HistoryItem } from './common/HistoryItem';
import { HistoryItemListWrapper } from './common/HistoryItemListWrapper';
import { ShowAllButton } from './common/ShowAllButton';

const ProjectHistoryCaption: FunctionComponent = () => {
    const { translate } = useContext(ApplicationContext);

    return (
        <CardIconCaption
            color="project.main"
            icon={StickyNote2OutlinedIcon}
            text={translate('pages.user-project-history.caption')}
        />
    );
};

type ProjectHistoryContentProps = {
    readonly canUserAddProjectHistory?: boolean;
    readonly canUserEditProjectHistory?: boolean;
    readonly projectHistory?: ReadonlyArray<ProjectHistoryItem>;
    readonly userId?: string;
};

export const ProjectHistoryContent: FunctionComponent<ProjectHistoryContentProps> = ({
    canUserAddProjectHistory = false,
    canUserEditProjectHistory = false,
    projectHistory = [],
    userId,
}) => {
    const { actions, translate } = useContext(ApplicationContext);
    const [showAll, setShowAll] = useState(false);
    const hasProjectHistory = projectHistory.length > 0;
    const slicedProjects = showAll ? projectHistory : projectHistory.slice(0, 2);

    const headlineActions = canUserAddProjectHistory && is.nonEmptyString(userId) && (
        <AddButton onClick={() => actions.navigateTo(createUserProjectHistoryUrl(userId))} />
    );

    const editAction = (itemId: string): VoidFunction | undefined =>
        canUserEditProjectHistory && is.nonEmptyString(userId)
            ? () => actions.navigateTo(editUserProjectHistoryUrl(userId, itemId))
            : undefined;

    useScrollToLocationHash();

    return (
        <HistoryItemListWrapper
            anchorId="project-history"
            headline={translate('pages.recruiter-profile.background.project-history.title')}
            headlineActions={headlineActions}
        >
            {hasProjectHistory ? (
                slicedProjects.map((projectItem) => (
                    <HistoryItem
                        key={projectItem.id}
                        caption={<ProjectHistoryCaption />}
                        dateFrom={projectItem.dateFrom}
                        dateTo={projectItem.dateTo}
                        description={projectItem.description}
                        skills={projectItem.skills}
                        skillsHeadline={translate('pages.user-project-history.skills-list-headline')}
                        subTitle={[projectItem.companyName, projectItem.location]}
                        title={projectItem.projectTitle}
                        onClickEdit={editAction(projectItem.id)}
                    />
                ))
            ) : (
                <CardPlaceholder
                    placeholderIcon={<WorkspacePremiumRoundedIcon />}
                    placeholderText={translate('pages.recruiter-profile.background.project-history.placeholder')}
                />
            )}
            {projectHistory.length > 2 && <ShowAllButton showAll={showAll} onClick={() => setShowAll(!showAll)} />}
        </HistoryItemListWrapper>
    );
};

type ProjectHistoryProps = {
    readonly userId: string;
};

export const ProjectHistory: FunctionComponent<ProjectHistoryProps> = ({ userId }: ProjectHistoryProps) => {
    const { data: userHistoryOptions, isFetching: userHistoryOptionsIsFetching } = useUserHistoryOptionsQuery(userId);
    const { data: projectHistory, isFetching: projectHistoryIsFetching } = useProjectHistoryQuery(
        userId,
        userHistoryOptions?.canReadHistory ?? false,
    );

    if (userHistoryOptionsIsFetching || projectHistoryIsFetching) {
        return <InlineLoadingSpinner />;
    }

    return (
        <ProjectHistoryContent
            canUserAddProjectHistory={userHistoryOptions?.canEditHistory}
            canUserEditProjectHistory={userHistoryOptions?.canEditHistory}
            projectHistory={projectHistory}
            userId={userId}
        />
    );
};
